import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Edit, Eye, Trash2, Calendar, DollarSign, MapPin, Home, Clock, CheckCircle, XCircle, KeyRound, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ListingCard from '@/components/ListingCard';

interface Listing {
  id: string;
  title: string;
  description: string;
  address: string;
  price_egp: number;
  deposit_egp: number;
  unit_type: string;
  furnished: boolean;
  gender_rules: string;
  lease_min_months: number;
  available_from: string;
  photos: string[];
  approval_status: string;
  status: string;
  rental_status: string;
  created_at: string;
  updated_at: string;
  verified: boolean;
  universities: {
    name_en: string;
    name_ar: string;
    city: string;
  } | null;
}

const MyListings = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedListing, setSelectedListing] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (user) {
      fetchMyListings();
    }
  }, [user]);

  const fetchMyListings = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('listings')
        .select(`
          *,
          universities (
            name_en,
            name_ar,
            city
          )
        `)
        .eq('owner_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching my listings:', error);
        toast.error('Failed to load your listings');
        return;
      }

      console.log('Fetched listings:', data); // Debug log
      setListings(data || []);
    } catch (error) {
      console.error('Exception fetching my listings:', error);
      toast.error('Failed to load your listings');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (approval_status: string, status: string) => {
    console.log('Approval status:', approval_status, 'Status:', status); // Debug log
    
    if (!approval_status || approval_status === 'pending') {
      return (
        <Badge variant="secondary" className="text-yellow-700 bg-yellow-100 border-yellow-300">
          <Clock className="h-3 w-3 mr-1" />
          {t('pending_approval', 'في انتظار الموافقة', 'Pending Approval')}
        </Badge>
      );
    }
    
    if (approval_status === 'approved') {
      return (
        <Badge variant="secondary" className="text-green-700 bg-green-100 border-green-300">
          <CheckCircle className="h-3 w-3 mr-1" />
          {t('approved', 'مُعتمد', 'Approved')}
        </Badge>
      );
    }
    
    if (approval_status === 'rejected') {
      return (
        <Badge variant="secondary" className="text-red-700 bg-red-100 border-red-300">
          <XCircle className="h-3 w-3 mr-1" />
          {t('rejected', 'مرفوض', 'Rejected')}
        </Badge>
      );
    }

    return (
      <Badge variant="secondary" className="text-gray-700 bg-gray-100 border-gray-300">
        <Clock className="h-3 w-3 mr-1" />
        Unknown Status
      </Badge>
    );
  };

  const getUnitTypeLabel = (type: string) => {
    const labels = {
      apartment: { en: 'Apartment', ar: 'شقة' },
      room: { en: 'Room', ar: 'غرفة' },
      studio: { en: 'Studio', ar: 'استوديو' },
      house: { en: 'House', ar: 'منزل' }
    };
    return labels[type as keyof typeof labels]?.[language === 'ar' ? 'ar' : 'en'] || type;
  };

  const getGenderLabel = (gender: string) => {
    const labels = {
      any: { en: 'Any', ar: 'أي جنس' },
      female: { en: 'Female Only', ar: 'إناث فقط' },
      male: { en: 'Male Only', ar: 'ذكور فقط' }
    };
    return labels[gender as keyof typeof labels]?.[language === 'ar' ? 'ar' : 'en'] || gender;
  };

  const handleViewListing = (listing: Listing) => {
    // Transform listing data to match the format expected by ListingCard
    const transformedListing = {
      id: listing.id,
      title: listing.title,
      titleAr: listing.title,
      price: listing.price_egp,
      deposit: listing.deposit_egp,
      university: listing.universities?.name_en || 'Unknown University',
      distance: 0, // Set as 0 for own listings since it's from campus
      furnished: listing.furnished,
      verified: listing.verified,
      images: listing.photos && listing.photos.length > 0 ? listing.photos : ['/placeholder.svg'],
      unitType: listing.unit_type === 'apartment' ? 'entire' : listing.unit_type,
      genderRules: listing.gender_rules === 'any' ? 'mixed' : listing.gender_rules,
      responseRate: 95, // Set high response rate for own listings
      ownerName: t('you', 'أنت', 'You'),
      description: listing.description,
      address: listing.address,
      amenities: [],
      availableFrom: listing.available_from,
      leaseMinMonths: listing.lease_min_months,
      roommatesAllowed: false,
    };
    
    setSelectedListing(transformedListing);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedListing(null);
  };

  const handleEditListing = (listingId: string) => {
    navigate(`/edit-listing/${listingId}`);
  };

  const handleRentStatusToggle = async (listingId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'available' ? 'rented' : 'available';
    
    try {
      const { error } = await supabase
        .from('listings')
        .update({ 
          rental_status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', listingId)
        .eq('owner_id', user?.id); // Security check

      if (error) {
        console.error('Error updating rental status:', error);
        toast.error(t('failed_update_status', 'فشل في تحديث حالة الإيجار', 'Failed to update rental status'));
        return;
      }

      // Update local state
      setListings(prev => prev.map(listing => 
        listing.id === listingId 
          ? { ...listing, rental_status: newStatus }
          : listing
      ));

      const statusText = newStatus === 'rented' 
        ? t('rented', 'مؤجر', 'Rented')
        : t('available', 'متاح', 'Available');
      
      toast.success(t('status_updated', 'تم تحديث الحالة بنجاح', 'Status updated successfully'), {
        description: t('property_marked_as', 'تم تعيين العقار كـ', `Property marked as ${statusText.toLowerCase()}`)
      });
    } catch (error) {
      console.error('Exception updating rental status:', error);
      toast.error(t('failed_update_status', 'فشل في تحديث حالة الإيجار', 'Failed to update rental status'));
    }
  };

  const handleVisibilityToggle = async (listingId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'hidden' : 'active';
    
    try {
      const { error } = await supabase
        .from('listings')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', listingId)
        .eq('owner_id', user?.id); // Security check

      if (error) {
        console.error('Error updating visibility:', error);
        toast.error(t('failed_update_visibility', 'فشل في تحديث الرؤية', 'Failed to update visibility'));
        return;
      }

      // Update local state
      setListings(prev => prev.map(listing => 
        listing.id === listingId 
          ? { ...listing, status: newStatus }
          : listing
      ));

      const visibilityText = newStatus === 'active' 
        ? t('visible', 'مرئي', 'visible in searches')
        : t('hidden', 'مخفي', 'hidden from searches');
      
      toast.success(t('visibility_updated', 'تم تحديث الرؤية', 'Visibility updated'), {
        description: t('property_is_now', 'العقار الآن', `Property is now ${visibilityText}`)
      });
    } catch (error) {
      console.error('Exception updating visibility:', error);
      toast.error(t('failed_update_visibility', 'فشل في تحديث الرؤية', 'Failed to update visibility'));
    }
  };

  const getRentalStatusBadge = (rental_status: string) => {
    if (rental_status === 'rented') {
      return (
        <Badge variant="secondary" className="text-blue-700 bg-blue-100 border-blue-300">
          <KeyRound className="h-3 w-3 mr-1" />
          {t('rented', 'مؤجر', 'Rented')}
        </Badge>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (listings.length === 0) {
    return (
      <div className="text-center py-12">
        <Home className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
        <h3 className="text-xl font-semibold mb-2">
          {t('no_listings_yet', 'لا توجد عقارات بعد', 'No Listings Yet')}
        </h3>
        <p className="text-muted-foreground mb-6">
          {t('create_first_listing', 'أنشئ أول عقار لك لبدء تأجيره للطلاب', 'Create your first listing to start renting to students')}
        </p>
        <Button onClick={() => navigate('/list-property')}>
          {t('list_property', 'أدرج عقارك', 'List Property')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-muted-foreground">
          {t('total_listings', 'إجمالي العقارات', 'Total listings')}: {listings.length}
        </p>
        <Button onClick={() => navigate('/list-property')}>
          {t('add_new_listing', 'أضف عقار جديد', 'Add New Listing')}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {listings.map((listing) => (
          <Card key={listing.id} className="overflow-hidden">
            <div className="relative h-48">
              {listing.photos?.[0] ? (
                <img 
                  src={listing.photos[0]} 
                  alt={listing.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  <Home className="h-12 w-12 text-muted-foreground" />
                </div>
              )}
              
              <div className="absolute top-3 left-3 flex flex-col gap-2">
                {getStatusBadge(listing.approval_status, listing.status)}
                {getRentalStatusBadge(listing.rental_status)}
              </div>

              <div className="absolute top-3 right-3 flex flex-col gap-2">
                {listing.furnished && (
                  <Badge variant="secondary">
                    {t('furnished', 'مفروش', 'Furnished')}
                  </Badge>
                )}
                {listing.status === 'hidden' && (
                  <Badge variant="secondary" className="text-gray-600 bg-gray-100 border-gray-300">
                    <EyeOff className="h-3 w-3 mr-1" />
                    {t('hidden', 'مخفي', 'Hidden')}
                  </Badge>
                )}
              </div>
            </div>

            <CardHeader className="pb-3">
              <CardTitle className="text-lg line-clamp-1">{listing.title}</CardTitle>
              <CardDescription className="line-clamp-2">
                {listing.description}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span className="truncate">
                  {listing.universities?.city || listing.address}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <span className="font-semibold text-lg">
                  {listing.price_egp.toLocaleString()} EGP
                </span>
                <span className="text-sm text-muted-foreground">
                  /{t('month', 'شهر', 'month')}
                </span>
              </div>

              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="text-xs">
                  {getUnitTypeLabel(listing.unit_type)}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {getGenderLabel(listing.gender_rules)}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {listing.lease_min_months} {t('months_min', 'شهور كحد أدنى', 'months min')}
                </Badge>
              </div>

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>
                  {t('available_from', 'متاح من', 'Available from')}: {' '}
                  {new Date(listing.available_from).toLocaleDateString()}
                </span>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1"
                  onClick={() => handleViewListing(listing)}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  {t('view', 'عرض', 'View')}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1"
                  onClick={() => handleEditListing(listing.id)}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  {t('edit', 'تعديل', 'Edit')}
                </Button>
              </div>

              {/* Rental Status Button */}
              <div className="pt-2">
                <Button 
                  variant={listing.rental_status === 'rented' ? 'default' : 'outline'}
                  size="sm" 
                  className="w-full"
                  onClick={() => handleRentStatusToggle(listing.id, listing.rental_status)}
                >
                  <KeyRound className="h-4 w-4 mr-2" />
                  {listing.rental_status === 'rented' 
                    ? t('mark_available', 'تعيين كمتاح', 'Mark as Available')
                    : t('mark_rented', 'تعيين كمؤجر', 'Mark as Rented')
                  }
                </Button>
              </div>

              {/* Visibility Toggle */}
              <div className="flex items-center justify-between pt-3 border-t">
                <Label htmlFor={`visibility-${listing.id}`} className="text-sm font-medium">
                  {t('show_in_search', 'إظهار في البحث', 'Show in search')}
                </Label>
                <Switch
                  id={`visibility-${listing.id}`}
                  checked={listing.status === 'active'}
                  onCheckedChange={() => handleVisibilityToggle(listing.id, listing.status)}
                  disabled={listing.approval_status !== 'approved'} // Only allow toggle for approved listings
                />
              </div>
              
              {listing.approval_status !== 'approved' && (
                <p className="text-xs text-muted-foreground">
                  {t('visibility_note', 'يمكن تغيير الرؤية فقط للعقارات المعتمدة', 'Visibility can only be changed for approved listings')}
                </p>
              )}

              <div className="text-xs text-muted-foreground">
                {t('created', 'تم الإنشاء', 'Created')}: {new Date(listing.created_at).toLocaleDateString()}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* View Listing Modal */}
      {selectedListing && (
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-md p-0 gap-0">
            <div className="max-h-[80vh] overflow-y-auto">
              <ListingCard 
                listing={selectedListing} 
                currentLang={language}
                onViewClick={() => {
                  handleCloseModal();
                  navigate(`/listing/${selectedListing.id}`);
                }}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default MyListings;
